import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Shield, 
  Activity,
  RefreshCw,
  Bitcoin,
  PieChart,
  Scale,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';

interface TokenMetrics {
  symbol: string;
  name: string;
  current_price_aud: number;
  current_price_btc: number;
  market_cap: number;
  fdv: number;
  fdv_mcap_ratio: number;
  fdv_warning: string | null;
  price_change_24h_aud: number;
  price_change_24h_btc: number;
  price_change_7d_aud: number;
  price_change_7d_btc: number;
  btc_relative_strength_24h: number;
  btc_relative_strength_7d: number;
  btc_correlation_30d: number;
  relative_strength_signal: string;
  diversification_value: string;
  float_percentage: number;
}

interface AdvancedMetricsResponse {
  success: boolean;
  timestamp: string;
  rate_limited?: boolean;
  cached?: boolean;
  cached_at?: string;
  btc_reference: {
    price_change_24h: number;
    price_change_7d: number;
  };
  metrics: TokenMetrics[];
  insights: {
    strong_outperformers: Array<{ symbol: string; name: string; btc_relative_7d: number }>;
    high_dilution_risk: Array<{ symbol: string; name: string; fdv_ratio: number; warning: string }>;
    best_diversification: Array<{ symbol: string; name: string; correlation: number }>;
  };
}

export function AdvancedAnalytics() {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isLoading, refetch } = useQuery<AdvancedMetricsResponse>({
    queryKey: ['advanced-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('calculate-advanced-metrics');
      if (error) throw error;
      
      // If rate limited with empty metrics, try DB cache directly
      if (data?.rate_limited && (!data?.metrics || data.metrics.length === 0) && !data?.cached) {
        const { data: cachedRows } = await supabase
          .from('cached_advanced_metrics' as any)
          .select('metrics_data, calculated_at')
          .order('calculated_at', { ascending: false })
          .limit(1);
        
        const cached = (cachedRows as any)?.[0];
        if (cached?.metrics_data) {
          return { ...cached.metrics_data, cached: true, cached_at: cached.calculated_at } as AdvancedMetricsResponse;
        }
      }
      
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast.success('Advanced metrics refreshed');
    } catch (error) {
      toast.error('Failed to refresh metrics');
    } finally {
      setIsRefreshing(false);
    }
  };

  const getRelativeStrengthColor = (signal: string) => {
    switch (signal) {
      case 'STRONG_OUTPERFORM': return 'text-green-500';
      case 'OUTPERFORM': return 'text-green-400';
      case 'STRONG_UNDERPERFORM': return 'text-red-500';
      case 'UNDERPERFORM': return 'text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getRelativeStrengthIcon = (signal: string) => {
    switch (signal) {
      case 'STRONG_OUTPERFORM':
      case 'OUTPERFORM':
        return <ArrowUpRight className="w-4 h-4" />;
      case 'STRONG_UNDERPERFORM':
      case 'UNDERPERFORM':
        return <ArrowDownRight className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };

  const getDiversificationBadge = (value: string) => {
    switch (value) {
      case 'HIGH':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">High Value</Badge>;
      case 'MEDIUM':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">Medium</Badge>;
      default:
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">Low Value</Badge>;
    }
  };

  const getFdvRatioBadge = (ratio: number) => {
    if (ratio >= 10) {
      return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">🚨 Critical</Badge>;
    } else if (ratio >= 5) {
      return <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">⚠️ High Risk</Badge>;
    } else if (ratio >= 3) {
      return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">⚡ Caution</Badge>;
    } else if (ratio >= 2) {
      return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30">📋 Monitor</Badge>;
    }
    return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">✅ Safe</Badge>;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const hasData = data?.metrics && data.metrics.length > 0;

  if (!isLoading && !hasData) {
    return (
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Advanced Analytics
          </CardTitle>
          <CardDescription>
            BTC Relative Strength, FDV Analysis & Correlation Matrix
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
            <Activity className="w-10 h-10 text-muted-foreground" />
            <p className="text-muted-foreground text-sm">
              Data will appear after the first successful refresh — try again in 1-2 minutes.
            </p>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Try Now
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Advanced Analytics
              {data?.cached && (
                <Badge variant="outline" className="text-xs font-normal ml-2">
                  Cached {data.cached_at ? `· ${Math.round((Date.now() - new Date(data.cached_at).getTime()) / 60000)}m ago` : ''}
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              BTC Relative Strength, FDV Analysis & Correlation Matrix
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {data?.btc_reference && (
          <div className="flex items-center gap-4 mt-4 p-3 rounded-lg bg-muted/50">
            <Bitcoin className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">BTC Reference:</span>
            <span className={`text-sm font-medium ${data.btc_reference.price_change_24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              24h: {data.btc_reference.price_change_24h >= 0 ? '+' : ''}{data.btc_reference.price_change_24h.toFixed(1)}%
            </span>
            <span className={`text-sm font-medium ${data.btc_reference.price_change_7d >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              7d: {data.btc_reference.price_change_7d >= 0 ? '+' : ''}{data.btc_reference.price_change_7d.toFixed(1)}%
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="relative-strength">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="relative-strength" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">BTC Relative Strength</span>
              <span className="sm:hidden">RS</span>
            </TabsTrigger>
            <TabsTrigger value="fdv-analysis" className="gap-2">
              <Scale className="w-4 h-4" />
              <span className="hidden sm:inline">FDV Analysis</span>
              <span className="sm:hidden">FDV</span>
            </TabsTrigger>
            <TabsTrigger value="correlation" className="gap-2">
              <PieChart className="w-4 h-4" />
              <span className="hidden sm:inline">Correlation</span>
              <span className="sm:hidden">Corr</span>
            </TabsTrigger>
          </TabsList>

          {/* BTC Relative Strength Tab */}
          <TabsContent value="relative-strength" className="mt-4">
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-transparent border border-green-500/20">
                <h4 className="font-semibold text-green-500 flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4" />
                  Outperforming BTC (7d)
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  These tokens are gaining value even when priced in BTC — true strength signals.
                </p>
                <div className="grid gap-2">
                  {data?.insights.strong_outperformers.slice(0, 8).map((token) => (
                    <div key={token.symbol} className="flex items-center justify-between p-2 rounded bg-background/50">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm">{token.symbol}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[120px]">{token.name}</span>
                      </div>
                      <span className="text-green-500 font-semibold text-sm">
                        +{token.btc_relative_7d.toFixed(1)}% vs BTC
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/20">
                <h4 className="font-semibold text-red-500 flex items-center gap-2 mb-3">
                  <TrendingDown className="w-4 h-4" />
                  Underperforming BTC (7d)
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  These tokens are losing value relative to BTC — avoid for swing trades.
                </p>
                <div className="grid gap-2">
                  {data?.metrics
                    .filter(t => t.relative_strength_signal === 'STRONG_UNDERPERFORM')
                    .slice(0, 5)
                    .map((token) => (
                      <div key={token.symbol} className="flex items-center justify-between p-2 rounded bg-background/50">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm">{token.symbol}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">{token.name}</span>
                        </div>
                        <span className="text-red-500 font-semibold text-sm">
                          {token.btc_relative_strength_7d.toFixed(1)}% vs BTC
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* FDV Analysis Tab */}
          <TabsContent value="fdv-analysis" className="mt-4">
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20">
                <h4 className="font-semibold text-orange-500 flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4" />
                  High Dilution Risk Tokens
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  These tokens have FDV/MCap ratio ≥ 5x — significant supply inflation ahead.
                </p>
                <div className="grid gap-2">
                  {data?.insights.high_dilution_risk.slice(0, 8).map((token) => (
                    <div key={token.symbol} className="p-3 rounded bg-background/50 space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-sm">{token.symbol}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[100px]">{token.name}</span>
                        </div>
                        <span className="text-orange-500 font-semibold text-sm">
                          {token.fdv_ratio.toFixed(1)}x
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{token.warning}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/50 border">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <Scale className="w-4 h-4 text-primary" />
                  FDV/MCap Ratio Guide
                </h4>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>1.0x - 2.0x</span>
                    <Badge className="bg-green-500/20 text-green-500">Safe - Most supply circulating</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>2.0x - 3.0x</span>
                    <Badge className="bg-blue-500/20 text-blue-500">Monitor vesting schedule</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>3.0x - 5.0x</span>
                    <Badge className="bg-yellow-500/20 text-yellow-500">Caution - 66%+ locked</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>5.0x - 10x</span>
                    <Badge className="bg-orange-500/20 text-orange-500">High Risk - 80%+ locked</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>10x+</span>
                    <Badge className="bg-red-500/20 text-red-500">Critical - 90%+ locked</Badge>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Correlation Matrix Tab */}
          <TabsContent value="correlation" className="mt-4">
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-transparent border border-blue-500/20">
                <h4 className="font-semibold text-blue-500 flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4" />
                  Best Diversification Picks
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Low BTC correlation tokens — these tend to hold value when BTC dips.
                </p>
                <div className="grid gap-2">
                  {data?.insights.best_diversification.slice(0, 8).map((token) => (
                    <div key={token.symbol} className="flex items-center justify-between p-2 rounded bg-background/50">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-sm">{token.symbol}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-[120px]">{token.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-500 font-semibold text-sm">
                          {(token.correlation * 100).toFixed(0)}% corr
                        </span>
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                          Diversify
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/50 border">
                <h4 className="font-semibold flex items-center gap-2 mb-3">
                  <PieChart className="w-4 h-4 text-primary" />
                  Correlation Interpretation
                </h4>
                <div className="grid gap-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>0% - 40%</span>
                    <Badge className="bg-green-500/20 text-green-500">High Diversification Value</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>40% - 60%</span>
                    <Badge className="bg-yellow-500/20 text-yellow-500">Moderate Correlation</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>60% - 100%</span>
                    <Badge className="bg-red-500/20 text-red-500">High Correlation (moves with BTC)</Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  💡 <strong>Pro tip:</strong> In a diversified swing portfolio, include tokens with different correlation profiles. 
                  When BTC corrects, low-correlation tokens may hold value better.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Full Metrics Table */}
        {data?.metrics && (
          <div className="mt-6 overflow-x-auto">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Top 20 Tokens - Full Metrics
            </h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-2">Token</th>
                  <th className="text-right p-2">Price (AUD)</th>
                  <th className="text-right p-2">7d vs BTC</th>
                  <th className="text-right p-2">FDV/MCap</th>
                  <th className="text-right p-2">Correlation</th>
                  <th className="text-center p-2">Signal</th>
                </tr>
              </thead>
              <tbody>
                {data.metrics.slice(0, 20).map((token) => (
                  <tr key={token.symbol} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="p-2">
                      <div>
                        <span className="font-mono font-bold">{token.symbol}</span>
                        <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">{token.name}</span>
                      </div>
                    </td>
                    <td className="text-right p-2 font-mono">
                      A${token.current_price_aud < 1 ? token.current_price_aud.toFixed(4) : token.current_price_aud.toFixed(2)}
                    </td>
                    <td className={`text-right p-2 font-semibold ${token.btc_relative_strength_7d >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {token.btc_relative_strength_7d >= 0 ? '+' : ''}{token.btc_relative_strength_7d.toFixed(1)}%
                    </td>
                    <td className="text-right p-2">
                      {getFdvRatioBadge(token.fdv_mcap_ratio)}
                    </td>
                    <td className="text-right p-2">
                      {(token.btc_correlation_30d * 100).toFixed(0)}%
                    </td>
                    <td className="text-center p-2">
                      <span className={`flex items-center justify-center gap-1 ${getRelativeStrengthColor(token.relative_strength_signal)}`}>
                        {getRelativeStrengthIcon(token.relative_strength_signal)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
